import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import whiteOwlLogo from '../assets/LOGO WHITE.png';
import buddyMascotGif from '../assets/NewHome/buddy-mascot.gif';
import { API_CONFIG, APP_CONFIG, QUICK_SIGNUP_STORAGE_KEY } from '../config/envConfig';
import {
  clearQuickSignupSession,
  completeBuyNowSignup,
  isQuickSignupAuthSuccess,
  looksLikeWebsite,
  redirectToAppOnboarding,
  toCompanyWebsiteUrl,
} from '../utils/quickSignupAuth';

const stripWebsiteHost = (value = '') =>
  value.trim().replace(/^https?:\/\//i, '').replace(/\/+$/, '');

const OAUTH_ERROR_MESSAGES = {
  account_exists: 'Account with this email already exists. Please use another email.',
  user_already_exist: 'Account with this email already exists. Please use another email.',
  oauth_failed: 'Sign-in failed. Please try again.',
  personal_email_not_allowed: 'Please use your work email. Personal email addresses are not allowed.',
};

const readQuickSignupSession = (locationState) => {
  if (locationState?.anon_session_id || locationState?.oauth_token || locationState?.flow === 'payment') {
    return {
      flow: locationState.flow || null,
      anon_session_id: locationState.anon_session_id || null,
      oauth_token: locationState.oauth_token || null,
      suggested_website: stripWebsiteHost(locationState.suggested_website || ''),
      email: locationState.email || '',
      otp: locationState.otp || '',
      question: locationState.question || '',
    };
  }

  try {
    const raw = sessionStorage.getItem(QUICK_SIGNUP_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.anon_session_id && !parsed?.oauth_token && parsed?.flow !== 'payment') return null;
    return {
      flow: parsed.flow || null,
      anon_session_id: parsed.anon_session_id || null,
      oauth_token: parsed.oauth_token || null,
      suggested_website: stripWebsiteHost(parsed.suggested_website || ''),
      email: parsed.email || '',
      otp: parsed.otp || '',
      question: parsed.question || '',
    };
  } catch {
    return null;
  }
};


const STYLES = `
.wo-onboard{
  --ink:#171A1F;
  --muted:#6B7280;
  --line:#E8E6E1;
  --navy:#141C30;
  --page:#05070E;
  --panel-dark:linear-gradient(rgb(11, 18, 48) 0%, rgb(22, 34, 77) 100%);
  --card-dark:#17213F;
  --highlight:#3B63D9;
  min-height:100vh;
  background:var(--page);
  color:var(--ink);
  font-family:'Instrument Sans',sans-serif;
  line-height:1.5;
}
.wo-onboard *,.wo-onboard *::before,.wo-onboard *::after{box-sizing:border-box;margin:0;padding:0}
.wo-onboard button{font:inherit}
.wo-onboard a{color:inherit;text-decoration:none}

.wo-onboard-stage{
  min-height:100vh;
  display:flex;
  align-items:center;
  justify-content:center;
  padding:120px 24px 64px;
  position:relative;
  overflow:hidden;
}
.wo-onboard-stars{
  position:absolute;inset:0;pointer-events:none;opacity:.7;
  background-image:radial-gradient(1.2px 1.2px at 8% 14%,rgba(255,255,255,.5) 50%,transparent 51%),radial-gradient(1px 1px at 16% 42%,rgba(255,255,255,.35) 50%,transparent 51%),radial-gradient(1.4px 1.4px at 24% 78%,rgba(255,255,255,.4) 50%,transparent 51%),radial-gradient(1px 1px at 38% 18%,rgba(255,255,255,.45) 50%,transparent 51%),radial-gradient(1.2px 1.2px at 47% 62%,rgba(255,255,255,.3) 50%,transparent 51%),radial-gradient(1px 1px at 58% 28%,rgba(255,255,255,.4) 50%,transparent 51%),radial-gradient(1.5px 1.5px at 67% 8%,rgba(255,255,255,.5) 50%,transparent 51%),radial-gradient(1px 1px at 74% 54%,rgba(255,255,255,.32) 50%,transparent 51%),radial-gradient(1.2px 1.2px at 82% 36%,rgba(255,255,255,.45) 50%,transparent 51%),radial-gradient(1px 1px at 88% 72%,rgba(255,255,255,.35) 50%,transparent 51%),radial-gradient(1.3px 1.3px at 93% 22%,rgba(255,255,255,.4) 50%,transparent 51%),radial-gradient(1px 1px at 6% 88%,rgba(255,255,255,.28) 50%,transparent 51%);
}
.wo-onboard-card-wrap{position:relative;z-index:1;width:100%;max-width:1140px}
.wo-onboard-mascot-frame{
  position:absolute;left:50%;bottom:100%;
  transform:translateX(-50%);/
  margin-bottom:-6px;
  width:150px;height:106px;
  overflow:hidden;
  z-index:1;
  pointer-events:none;
}
.wo-onboard-mascot{display:block;width:150px;height:auto;filter:drop-shadow(0 14px 22px rgba(4,10,28,.45))}

.wo-onboard-shell{
  position:relative;z-index:2;
  display:grid;
  grid-template-columns:minmax(0,1fr) minmax(0,1fr);
  border-radius:28px;
  overflow:hidden;
  box-shadow:0 40px 90px rgba(2,6,20,.55);
}

/* ---- Left ---- */
.wo-onboard-left{
  display:flex;
  flex-direction:column;
  padding:36px 44px 28px;
  background:var(--panel-dark);
  color:#EFF3FB;
}
.wo-onboard-brand{display:inline-flex;align-items:center;align-self:flex-start}
.wo-onboard-logo{height:24px;width:auto;display:block}

.wo-onboard-left-body{
  flex:1;
  display:flex;
  flex-direction:column;
  justify-content:center;
  width:100%;
  padding:28px 0 20px;
}
.wo-onboard-left h1{
  font-family:'Fraunces',serif;
  font-weight:600;
  font-size:clamp(1.5rem,2.4vw,1.85rem);
  line-height:1.25;
  margin-bottom:14px;
  color:#FAFBFF;
}
.wo-onboard-left h1 em{
  font-style:italic;
  color:#F5A94B;
  font-weight:600;
}
.wo-onboard-lead{
  margin-bottom:22px;
  font-size:0.92rem;
  line-height:1.55;
  color:rgba(239,243,251,.65);
  max-width:42ch;
}

.wo-onboard-feed{
  display:flex;
  flex-direction:column;
  gap:8px;
  list-style:none;
}
.wo-onboard-feed-item{
  display:flex;
  align-items:flex-start;
  gap:12px;
  padding:12px 14px;
  border-radius:14px;
  background:var(--card-dark);
}
.wo-onboard-feed-icon{
  width:32px;height:32px;border-radius:9px;flex-shrink:0;
  display:flex;align-items:center;justify-content:center;
  color:#C7D6FF;background:rgba(143,180,255,.14);
}
.wo-onboard-feed-icon svg{display:block;width:16px;height:16px}
.wo-onboard-feed-copy{min-width:0;flex:1}
.wo-onboard-feed-heading{
  display:block;font-size:0.92rem;font-weight:600;color:#FAFBFF;margin-bottom:3px;line-height:1.3;
}
.wo-onboard-feed-desc{
  font-size:0.82rem;line-height:1.45;color:rgba(239,243,251,.6);
}

.wo-onboard-left-foot{
  font-size:0.76rem;
  color:rgba(239,243,251,.4);
  padding-top:20px;
}

/* ---- Right ---- */
.wo-onboard-right{
  display:flex;
  align-items:center;
  justify-content:center;
  padding:48px 40px;
  background:#f7f8fc;
}
.wo-onboard-card{
  width:100%;
  max-width:400px;
  text-align:center;
}
.wo-onboard-mark{
  width:60px;
  height:60px;
  margin:0 auto 20px;
  border-radius:50%;
  background:var(--navy);
  color:#F5F5F5;
  display:flex;
  align-items:center;
  justify-content:center;
}
.wo-onboard-mark svg{width:28px;height:28px;display:block}
.wo-onboard-mark img{width:32px;height:32px;display:block;filter:brightness(0) invert(1)}

.wo-onboard-card h2{
  font-family:'Instrument Sans',sans-serif;
  font-weight:700;
  font-size:clamp(1.3rem,2vw,1.6rem);
  line-height:1.3;
  margin-bottom:12px;
  color:var(--ink);
}
.wo-onboard-sub{
  margin:0 auto 26px;
  font-size:0.92rem;
  line-height:1.5;
  color:#4E5B7E;
  max-width:34ch;
}

.wo-onboard-form{
  display:flex;
  flex-direction:column;
  align-items:stretch;
  gap:12px;
}
.wo-onboard-field{
  display:flex;
  align-items:center;
  width:100%;
  padding:13px 16px;
  border-radius:12px;
  border:1.5px solid transparent;
  background:#fff;
  transition:border-color .15s ease;
  cursor:text;
}
.wo-onboard-field:focus-within{
  border-color:#A9C4FD;
  outline:none;
}
.wo-onboard-field.is-error{border-color:#1B2E59}
.wo-onboard-field input{
  flex:1;
  min-width:0;
  border:none;
  outline:none;
  box-shadow:none;
  background:transparent;
  font-family:inherit;
  font-size:0.96rem;
  color:var(--ink);
  line-height:1.4;
}
.wo-onboard-field input:focus{outline:none;box-shadow:none}
.wo-onboard-field input::placeholder{color:#A1A1AA}
.wo-onboard-field input:-webkit-autofill,
.wo-onboard-field input:-webkit-autofill:hover,
.wo-onboard-field input:-webkit-autofill:focus{
  -webkit-box-shadow:0 0 0 1000px #fff inset;
  -webkit-text-fill-color:var(--ink);
  transition:background-color 5000s ease-in-out 0s;
}

.wo-onboard-error{
  font-size:0.8rem;
  color:#C0392B;
  text-align:left;
  margin-top:-4px;
}

.wo-onboard button.wo-onboard-cta{
  width:100%;
  padding:14px 18px;
  border:none;
  border-radius:12px;
  background:var(--navy);
  color:#fff;
  font-size:0.96rem;
  font-weight:600;
  cursor:pointer;
  transition:transform .15s ease,background .15s ease;
}
.wo-onboard button.wo-onboard-cta:hover{background:#0C1220;transform:translateY(-1px);color:#fff}
.wo-onboard button.wo-onboard-cta:disabled{opacity:.65;cursor:not-allowed;transform:none}

.wo-onboard-skip{
  align-self:center;
  border:none;
  background:none;
  color:var(--highlight);
  font-size:0.9rem;
  cursor:pointer;
  padding:4px 8px;
}
.wo-onboard-skip:hover{text-decoration:underline}
.wo-onboard-skip:disabled{opacity:.65;cursor:not-allowed;text-decoration:none}

.wo-onboard-credits{
  margin-top:22px;
  border:1px solid var(--line);
  border-radius:14px;
  background:#fff;
  padding:16px 18px;
  text-align:center;
}
.wo-onboard-credits-top{
  display:flex;
  align-items:center;
  justify-content:center;
  gap:8px;
  margin-bottom:8px;
  flex-wrap:wrap;
}
.wo-onboard-credits-top strong{
  font-size:0.95rem;
  font-weight:600;
  color:var(--ink);
}
.wo-onboard-free{
  display:inline-flex;
  align-items:center;
  padding:2px 9px;
  border-radius:999px;
  background:#E4F8EA;
  color:#15803D;
  font-size:0.72rem;
  font-weight:700;
  line-height:1.4;
}
.wo-onboard-credits p{
  font-size:0.82rem;
  line-height:1.45;
  color:var(--muted);
}

@media (max-width:960px){
  .wo-onboard-stage{padding:96px 16px 48px}
  .wo-onboard-shell{grid-template-columns:1fr;border-radius:22px}
  .wo-onboard-left{padding:28px 24px 20px}
  .wo-onboard-left-body{padding:20px 0 8px}
  .wo-onboard-right{padding:40px 24px 48px}
  .wo-onboard-card{max-width:420px}
}
`;

const FEATURES = [
  {
    id: 'signals',
    title: 'Account Signals',
    text: 'Get notified the moment a target account shows buying intent.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M13 2L4 14h7l-1 8 10-14h-7V2z" fill="currentColor" />
      </svg>
    ),
  },
  {
    id: 'intel',
    title: 'Contact Intel',
    text: 'Match scores, insight summaries, and activity timelines for every contact.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <circle cx="9" cy="8" r="3" stroke="currentColor" strokeWidth="1.8" />
        <circle cx="16.5" cy="9" r="2.4" stroke="currentColor" strokeWidth="1.8" />
        <path d="M3.5 19c.8-3 2.9-4.5 5.5-4.5S14 16 14.8 19" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        <path d="M14.5 17.2c.7-1.6 2-2.4 3.8-2.4 1.4 0 2.5.5 3.2 1.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    id: 'buddy',
    title: 'WO Buddy',
    text: 'An AI assistant that drafts outreach from real account context.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <rect x="5" y="7" width="14" height="11" rx="3" stroke="currentColor" strokeWidth="1.8" />
        <circle cx="9.5" cy="12.5" r="1.2" fill="currentColor" />
        <circle cx="14.5" cy="12.5" r="1.2" fill="currentColor" />
        <path d="M12 3v3M9 18.5v1.5M15 18.5v1.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    id: 'crm',
    title: 'CRM Sync',
    text: 'Two-way sync with HubSpot and Salesforce, always up to date.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M7 7H4v3M17 17h3v-3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M5 10a7 7 0 0 1 12.2-4.2L20 8M19 14a7 7 0 0 1-12.2 4.2L4 16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
];

const StarIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M12 3l2.6 5.6 6.1.7-4.5 4.2 1.2 6-5.4-3-5.4 3 1.2-6-4.5-4.2 6.1-.7L12 3z" fill="#E8A136" />
  </svg>
);

const Onboarding = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [quickSignup, setQuickSignup] = useState(() => readQuickSignupSession(location.state));
  const [website, setWebsite] = useState(
    () => stripWebsiteHost(
      new URLSearchParams(location.search).get('suggested_website')
      || readQuickSignupSession(location.state)?.suggested_website
      || ''
    )
  );
  const [error, setError] = useState('');
  const [touched, setTouched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checkEmailMessage, setCheckEmailMessage] = useState('');
  const [bootstrapping, setBootstrapping] = useState(() => {
    const params = new URLSearchParams(location.search);
    return params.has('oauth_token')
      || params.get('resume_onboarding') === 'true'
      || params.has('error');
  });

  const isValid = useMemo(() => looksLikeWebsite(website), [website]);
  const anonSessionId = quickSignup?.anon_session_id || null;
  const oauthToken = quickSignup?.oauth_token || null;
  const paymentEmail = quickSignup?.flow === 'payment' ? (quickSignup?.email || '') : '';
  const paymentOtp = quickSignup?.flow === 'payment' ? (quickSignup?.otp || '') : '';
  const isPaymentFlow = Boolean(paymentEmail && paymentOtp);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const token = params.get('oauth_token');
    const resume = params.get('resume_onboarding') === 'true';
    const email = params.get('email') || '';
    const err = params.get('error');
    const suggested = stripWebsiteHost(params.get('suggested_website') || '');

    if (!token && !resume && !err) {
      setBootstrapping(false);
      return;
    }

    const clearSearch = () => {
      navigate('/onboarding', { replace: true });
    };

    if (err) {
      setError(OAUTH_ERROR_MESSAGES[err] || 'Sign-in failed. Please try again.');
      setBootstrapping(false);
      clearSearch();
      return;
    }

    if (resume) {
      if (!email) {
        setError('Unable to resume onboarding. Please start again.');
        setBootstrapping(false);
        clearSearch();
        return;
      }

      setLoading(true);
      (async () => {
        try {
          await resumeOnboarding(email);
          setLoading(false);
          setBootstrapping(false);
          clearSearch();
        } catch (resumeErr) {
          setError(resumeErr.message || 'Unable to resume onboarding. Please try again.');
          setLoading(false);
          setBootstrapping(false);
          clearSearch();
        }
      })();
      return;
    }

    if (token) {
      const payload = {
        oauth_token: token,
        suggested_website: suggested,
        email,
      };
      sessionStorage.setItem(QUICK_SIGNUP_STORAGE_KEY, JSON.stringify(payload));
      setQuickSignup(payload);
      if (suggested) setWebsite(suggested);
      setBootstrapping(false);
      clearSearch();
    }
  }, [location.search, navigate]);

  const goToApp = () => {
    window.location.href = APP_CONFIG.getOnboardingRedirectUrl();
  };

  const resumeOnboarding = async (userEmail) => {
    const response = await fetch(API_CONFIG.quickSignupResumeUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: userEmail }),
    });
    const data = await response.json().catch(() => ({}));

    if (!response.ok || data.error) {
      throw new Error(data.error || data.message || 'Unable to resume onboarding. Please try again.');
    }

    clearQuickSignupSession();
    setCheckEmailMessage(data.message || 'Check your email for a link to verify your account.');
  };

  const handleContinue = async (e) => {
    e.preventDefault();
    setTouched(true);
    if (!isValid) {
      setError('Enter a valid company website (e.g. thewhiteowl.ai)');
      return;
    }
    if (!anonSessionId && !oauthToken && !isPaymentFlow) {
      setError('Your signup session expired. Please start again from the home page.');
      return;
    }

    setError('');
    setLoading(true);

    try {
      const companyWebsite = toCompanyWebsiteUrl(website);

      // Buy Now (post-payment): complete with email+otp, exchange the code for tokens, then hand off.
      if (isPaymentFlow) {
        await completeBuyNowSignup({
          email: paymentEmail,
          otp: paymentOtp,
          companyWebsite,
        });
        return;
      }

      const isOauth = Boolean(oauthToken);
      const response = await fetch(
        isOauth ? API_CONFIG.quickSignupOauthCompleteUrl : API_CONFIG.quickSignupCompleteUrl,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(
            isOauth
              ? { oauth_token: oauthToken, company_website: companyWebsite }
              : { anon_session_id: anonSessionId, company_website: companyWebsite }
          ),
        }
      );

      const data = await response.json().catch(() => ({}));

      if (data.resume_onboarding) {
        const email = data.email || quickSignup?.email;
        if (!email) {
          setError(data.message || 'Account already created. Please sign in again to continue.');
          return;
        }
        await resumeOnboarding(email);
        return;
      }

      if (isOauth) {
        if (!response.ok || data.error || !isQuickSignupAuthSuccess(data)) {
          setError(data.error || data.message || 'Unable to complete signup. Please try again.');
          return;
        }

        redirectToAppOnboarding({
          ...data,
          company_website: data.company_website || companyWebsite,
        });
        return;
      }

      if (!response.ok || data.error) {
        setError(data.error || data.message || 'Unable to complete signup. Please try again.');
        return;
      }

      clearQuickSignupSession();
      setCheckEmailMessage(data.message || 'Check your email for a link to verify your account.');
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fieldClass = [
    'wo-onboard-field',
    isValid ? 'is-valid' : '',
    touched && !isValid ? 'is-error' : '',
  ].filter(Boolean).join(' ');

  return (
    <div className="wo-onboard">
      <style>{STYLES}</style>
      <div className="wo-onboard-stage">
        <div className="wo-onboard-stars" aria-hidden="true" />
        <div className="wo-onboard-card-wrap">
          <div className="wo-onboard-mascot-frame" aria-hidden="true">
            <img src={buddyMascotGif} alt="" className="wo-onboard-mascot" />
          </div>

          <div className="wo-onboard-shell">
            <aside className="wo-onboard-left">
              <Link to="/" className="wo-onboard-brand" aria-label="WhiteOwl home">
                <img src={whiteOwlLogo} alt="WhiteOwl" className="wo-onboard-logo" /> 
              </Link>

              <div className="wo-onboard-left-body">
                <h1>
                  See the <em>signals</em> your consulting firm is missing.
                </h1>
                <p className="wo-onboard-lead">
                  WhiteOwl AI watches for the moments that matter, so you never miss an opportunity.
                </p>

                <ul className="wo-onboard-feed">
                  {FEATURES.map((feature) => (
                    <li key={feature.id} className="wo-onboard-feed-item">
                      <span className="wo-onboard-feed-icon">{feature.icon}</span>
                      <span className="wo-onboard-feed-copy">
                        <strong className="wo-onboard-feed-heading">{feature.title}</strong>
                        <p className="wo-onboard-feed-desc">{feature.text}</p>
                      </span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="wo-onboard-left-foot">
                © {new Date().getFullYear()} WhiteOwl
              </div>
            </aside>

            <section className="wo-onboard-right">
              <div className="wo-onboard-card">
                <div className="wo-onboard-mark" aria-hidden="true">
                  <img src="/logo512.png" alt="" />
                </div>
                {checkEmailMessage ? (
                  <>
                    <h2>Check your email</h2>
                    <p className="wo-onboard-sub">{checkEmailMessage}</p>
                  </>
                ) : (
                  <>
                    <h2>What&apos;s your company website?</h2>
                    <p className="wo-onboard-sub">
                      Start here. WhiteOwl reads your site and surfaces the accounts already showing buying intent.
                    </p>

                    {bootstrapping ? (
                      <p className="wo-onboard-sub">Finishing sign-in…</p>
                    ) : (
                      <form className="wo-onboard-form" onSubmit={handleContinue} noValidate>
                        <label className={fieldClass}>
                          <input
                            type="text"
                            value={website}
                            onChange={(e) => {
                              setWebsite(e.target.value);
                              if (error) setError('');
                            }}
                            onBlur={() => setTouched(true)}
                            placeholder="www.yourcompany.com"
                            autoComplete="url"
                            aria-label="Company website"
                            disabled={loading}
                          />
                        </label>
                        {error ? <p className="wo-onboard-error">{error}</p> : null}

                        <button type="submit" className="wo-onboard-cta" disabled={loading}>
                          {loading ? 'Continuing…' : 'Continue to WhiteOwl →'}
                        </button>
                      </form>
                    )}
                  </>
                )}

                {checkEmailMessage ? null : (
                  <div className="wo-onboard-credits">
                    <div className="wo-onboard-credits-top">
                      <StarIcon />
                      <strong>1,000 credits</strong>
                      <span className="wo-onboard-free">free</span>
                    </div>
                    <p>Use them to enrich contacts, research accounts, and power outreach with WO Buddy.</p>
                  </div>
                )}
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Onboarding;
